import { useState, useRef } from 'react'
import { decode } from '@googlemaps/polyline-codec'
import { shared } from '../assets/images'
import { useVehiclesService } from './useVehiclesService'
import { getSessionToken } from '../utils'

const { REACT_APP_SOCKET_URL } = process.env

const MAX_RETRIES = 5

const useWebSockets = ({ onEvent } = {}) => {
  const [position, setPosition] = useState({})
  const token = getSessionToken()
  const activeConnections = useRef({})
  const { checkSession } = useVehiclesService()
  const lastTurnOn = useRef({})
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  const emitEvent = (type, vehicle) => onEventRef.current?.({ type, vehicle })

  // Solo avisa cambios: el primer gps de cada vehículo fija el estado inicial.
  const detectTurnOnChange = (chasis, vehicle, turnOn, onLine) => {
    if (onLine !== '1') return
    const previous = lastTurnOn.current[chasis]
    lastTurnOn.current[chasis] = turnOn
    if (previous === undefined || previous === turnOn) return
    emitEvent(turnOn === '1' ? 'turnOn' : 'turnOff', vehicle)
  }

  function connecting(vehicle, tokenHeader, retryCount = 0) {
    const chasis = vehicle.Chasis || vehicle.chasis
    let ws

    if (tokenHeader) {
      ws = new WebSocket(
        `${REACT_APP_SOCKET_URL}/compartir?token=${tokenHeader}`
      )
    } else {
      ws = new WebSocket(
        `${REACT_APP_SOCKET_URL}?authorization=${JSON.parse(token)}&chasis=${
          vehicle.chasis
        }`
      )
    }

    activeConnections.current[chasis] = ws

    ws.onopen = () => {
      console.log('[WS] open', chasis, '| intento:', retryCount)
    }

    ws.onclose = (event) => {
      console.log(
        '[WS] cerrado:',
        chasis,
        '| code:',
        event.code,
        '| wasClean:',
        event.wasClean,
        '| intento:',
        retryCount
      )
      // code 1000 = cierre normal, no reconectar
      if (event.code === 1000) return
      // Un token vencido también cierra el socket: si es eso, se cierra la sesión.
      checkSession()
      if (retryCount >= MAX_RETRIES) {
        console.warn('[WS] Máximo de reintentos alcanzado para:', chasis)
        return
      }
      const delay = Math.min(1000 * Math.pow(2, retryCount), 30000)
      console.log(
        `[WS] Reconectando ${chasis} en ${delay}ms (intento ${
          retryCount + 1
        }/${MAX_RETRIES})`
      )
      setTimeout(() => {
        if (activeConnections.current[chasis] === ws) {
          connecting(vehicle, tokenHeader, retryCount + 1)
        }
      }, delay)
    }

    ws.onerror = (event) => {
      console.error('[WS] Error en WebSocket:', chasis, '| URL:', ws.url)
    }

    ws.onmessage = (event) => {
      if (event.data === 'bye') {
        delete activeConnections.current[chasis]
        ws.close(1000)
        return
      }
      const partes = event.data.split('¬')
      if (partes[0] === 'alm') {
        emitEvent('alarm', vehicle)
        return
      }
      if (partes[0] !== 'gps') {
        return
      }
      detectTurnOnChange(chasis, vehicle, partes[6], partes[7])
      const latitude = partes[3]
      if (typeof latitude === 'string') {
        const onChangeImage = () => {
          if (partes[7] === '1') {
            return partes[6] === '1' ? shared.motorON : shared.motorOFF
          }
          if (partes[7] === '2') {
            return shared.hhver
          }
          return shared.motorNO
        }
        const positionVehicle = decode(latitude, 5)
        try {
          const newPosition = {
            img: onChangeImage(),
            velocity: partes[4],
            turnOn: partes[6],
            latitude: positionVehicle[0][0],
            longitude: positionVehicle[0][1],
            meters: partes[5],
            onLine: partes[7],
            ...vehicle,
            chasis,
            angle: partes[2],
            time: +partes[1],
          }

          setPosition((prev) => ({
            ...prev,
            [chasis]: newPosition,
          }))
        } catch (err) {
          console.error('[WS] Error actualizando posición:', err)
        }
      }
    }
  }

  const useSocket = (vehicles, tokenHeader) => {
    if (!tokenHeader) {
      vehicles.forEach((vehicle) => connecting(vehicle))
    } else {
      connecting(vehicles, tokenHeader)
    }
  }

  const disconnect = () => {
    Object.values(activeConnections.current).forEach((ws) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(1000)
      }
    })
    activeConnections.current = {}
  }

  return { useSocket, position, disconnect }
}

export { useWebSockets }
